import { CompactProductLockup } from '@luminarium-ai/luminarium-core-ui';
import { Box, Text } from '@mantine/core';
import logoOnLight from '@/assets/logos/Luminarium_Logo_FullColour_Dark.svg';
import logoOnDark from '@/assets/logos/Luminarium_Logo_FullColour_Light.svg';

const PRODUCT_NAME = 'Discovery Dialogues';

export interface ProductLockupProps {
  /**
   * hero — stacked Luminarium wordmark, hairline rule, product name as an
   * Inter letterspaced label. The product's first brand touchpoint (login).
   * compact — orange star + product name in Bespoke Serif, name leading.
   * In-session only: the star may carry the brand here because the hero
   * has already introduced it at sign-in.
   */
  variant: 'hero' | 'compact';
  /** Surface behind the lockup: light = ivory/white, dark = navy. */
  ground: 'light' | 'dark';
}

/**
 * Discovery Dialogues product lockup (LUM-155).
 *
 * Convention: brand/v1/lockups/README.md in the brand repo — a Luminarium
 * product is branded as a lockup of the Luminarium identity + product name,
 * never as a separate mark. The star keeps brand orange on both grounds;
 * the product name is never set in orange (contrast) — navy or ivory only.
 */
export function ProductLockup({
  variant,
  ground,
}: Readonly<ProductLockupProps>) {
  if (variant === 'hero') {
    return (
      <Box
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img
          src={ground === 'dark' ? logoOnDark : logoOnLight}
          alt="Luminarium"
          style={{ height: 40, width: 'auto' }}
        />
        <Box
          style={{
            height: 1,
            width: '50%',
            marginTop: 15,
            marginBottom: 11,
            backgroundColor:
              ground === 'dark'
                ? 'rgba(250, 244, 232, 0.24)'
                : 'rgba(29, 36, 50, 0.18)',
          }}
        />
        <Text
          span
          fw={600}
          tt="uppercase"
          fz={12.5}
          c={ground === 'dark' ? 'navy.3' : 'navy.7'}
          style={{ letterSpacing: '0.28em', whiteSpace: 'nowrap' }}
        >
          {PRODUCT_NAME}
        </Text>
      </Box>
    );
  }

  return <CompactProductLockup ground={ground} productName={PRODUCT_NAME} />;
}
